import type { Node } from '@tiptap/pm/model';

import { EDITOR_WRITE_STEP, getToolSessionMetadata, INITIAL_STEP } from './agent-tool-session-metadata';
import type { AgentEditTarget } from '../edit-target';
import { createDocumentEditTarget } from '../edit-target';
import type { AutoScrollRegistration } from '../streaming/auto-scroll';

export type RevertTarget = { kind: 'document' } | { kind: 'range'; from: number; to: number };

export type RevertData = {
  beforeDoc: Node;
  restoreTarget: RevertTarget;
};

// Content this run has already persisted (an edit-phase direct write), and the editor writes that
// produced it. A size refusal of a later write in the same run restores this content.
export type DurableEdit = {
  doc: Node;
  toolCallIds: string[];
};

// Terminal completion outcome for a run. Defined here (a pure leaf) so both the finalize
// module and the completion-patch builder can import it without a cycle.
export type CompletionOutcome = 'completed' | 'direct_write' | 'review_ready' | 'write_finished';

// Structural type for the pending review commit stored on the run record. The richer
// CompletionResolution lives in the finalize module; the record only needs to read
// `outcome` to tell a persisted review commit apart at terminalization.
export type PendingReviewCommit = {
  runId: number;
  commit: Promise<{ outcome: CompletionOutcome }>;
};
export type EditorState = 'interactive' | 'agent_writing' | 'review';
export type AgentRunActivityState =
  | 'idle'
  | 'thinking'
  | 'reading'
  | 'writing'
  | 'awaiting_summary'
  | 'done'
  | 'cancelled'
  | 'errored';

export type AgentRunUiState = {
  editorState: EditorState;
  activityState: AgentRunActivityState;
  isLoading: boolean;
  currentStep: string | null;
  isNetworkError: boolean;
};

export type AgentRunTerminalizationSource = 'finish' | 'cancel' | 'error';

declare const agentRunIdentityBrand: unique symbol;

// A surrogate for the run record's object identity, which the staleness guards used to rely on.
// It identifies the run's lineage: `createAgentRunState` mints a fresh one per run and the
// immutable reducer copies (spread) preserve it, so a guard captured mid-run stays fresh across
// applied UI events yet goes stale once a new run is minted. Guards compare
// `current.identity !== captured.identity`.
export type AgentRunIdentity = { readonly [agentRunIdentityBrand]: true };

// The backend refused to run AI for a request for lack of AI credits. `prompt`: the refused prompt's text
// when the refused request was the turn's first, so the user can send it again later.
export type AiRefusal = { prompt?: string };

export type AgentRunEvent =
  | { type: 'request_started' }
  | { type: 'tool_progressed'; toolType: string }
  | { type: 'agent_write_started' }
  | { type: 'agent_write_finished' }
  | { type: 'review_ready' }
  | { type: 'review_resolved' }
  | { type: 'completed' }
  // `refusal`: a request the user started was refused; never set for an automatic continuation.
  | { type: 'cancelled'; refusal?: AiRefusal }
  // `content_too_large`: the end-of-run size check refused the edit (see `content-size-check.ts`).
  | { type: 'errored'; network: boolean; reason?: 'content_too_large' };

// Field ownership is split deliberately:
// - UI fields (`activityState`, `currentStep`, `isNetworkError`, `reviewPending`) are reducer-owned
//   and immutable: only `reduceAgentRunEvent` changes them, by returning a fresh record. The UI
//   projection reads exactly these fields, so a new record identity means a new UI snapshot.
// - Side fields (`editTarget`, `revertData`, `durableEdit`, `startedFromEmptyEditor`, `streamingEnabled`,
//   `terminalizationSource`, `pendingReviewCommit`, `scrollRegistration`) are store-owned and mutable: their setters mutate
//   the live record in place and never emit, since none feed the UI projection.
// - `identity` is run lineage: minted per run, preserved across immutable copies (see AgentRunIdentity).
export type AgentRunState = {
  currentRunId: number;
  identity: AgentRunIdentity;
  editTarget: AgentEditTarget;
  // Review presence is stored as durable `reviewPending` ("pendingReview committed and
  // not yet resolved"); the interactive/agent_writing/review `editorState` is derived from
  // `activityState` and `reviewPending` via `deriveEditorState`, not stored.
  reviewPending: boolean;
  activityState: AgentRunActivityState;
  currentStep: string | null;
  isNetworkError: boolean;
  terminalizationSource: AgentRunTerminalizationSource | null;
  revertData: RevertData | null;
  durableEdit: DurableEdit | null;
  startedFromEmptyEditor: boolean;
  streamingEnabled: boolean;
  pendingReviewCommit: PendingReviewCommit | null;
  scrollRegistration: AutoScrollRegistration | null;
};

export const DEFAULT_AGENT_RUN_UI_STATE: AgentRunUiState = {
  editorState: 'interactive',
  activityState: 'idle',
  isLoading: false,
  currentStep: null,
  isNetworkError: false,
};

export function isAgentRunLoading(run?: Pick<AgentRunState | AgentRunUiState, 'activityState'>): boolean {
  if (!run) return false;

  return (
    run.activityState === 'thinking' ||
    run.activityState === 'reading' ||
    run.activityState === 'writing' ||
    run.activityState === 'awaiting_summary'
  );
}

export function isAgentRunAwaitingPostEditSummary(run?: Pick<AgentRunState | AgentRunUiState, 'activityState'>): boolean {
  return !!run && run.activityState === 'awaiting_summary';
}

// Derives the observable editor lock state from the stored run. `agent_writing` is exactly
// `activityState === 'writing'` (only `agent_write_started` sets that activity); otherwise a
// committed durable review (`reviewPending`) means `review`; else the editor is `interactive`.
export function deriveEditorState(run?: Pick<AgentRunState, 'activityState' | 'reviewPending'>): EditorState {
  if (!run) return 'interactive';
  if (run.activityState === 'writing') return 'agent_writing';
  if (run.reviewPending) return 'review';
  return 'interactive';
}

export function createAgentRunState(currentRunId = 0): AgentRunState {
  return {
    currentRunId,
    identity: {} as AgentRunIdentity,
    editTarget: createDocumentEditTarget(),
    reviewPending: false,
    activityState: 'idle',
    currentStep: null,
    isNetworkError: false,
    terminalizationSource: null,
    revertData: null,
    durableEdit: null,
    startedFromEmptyEditor: false,
    streamingEnabled: true,
    pendingReviewCommit: null,
    scrollRegistration: null,
  };
}

export function getAgentRunUiSnapshot(run?: AgentRunState): AgentRunUiState {
  if (!run) return DEFAULT_AGENT_RUN_UI_STATE;

  // A freshly minted / idle run (only `createAgentRunState` produces `activityState === 'idle'`, and
  // it does so with no step/error/review) projects to the shared default. Return the constant so the
  // store's per-record memo keeps a stable reference across the no-run→idle and `startAgentRun` reset
  // transitions the deleted snapshot cache used to smooth over, avoiding a redundant re-render for an
  // unchanged projection.
  if (run.activityState === 'idle' && !run.reviewPending && run.currentStep === null && !run.isNetworkError) {
    return DEFAULT_AGENT_RUN_UI_STATE;
  }

  return {
    editorState: deriveEditorState(run),
    activityState: run.activityState,
    isLoading: isAgentRunLoading(run),
    currentStep: run.currentStep,
    isNetworkError: run.isNetworkError,
  };
}

// Owns the immutable UI slice of the run record: returns a NEW AgentRunState (spread copy, so
// `identity` and every store-owned side field are preserved) whenever an event applies, or `null`
// for the duplicate-suppression cases that leave the run unchanged. Only reducer events replace the
// record, so record identity changes exactly when a UI-relevant event applies.
export function reduceAgentRunEvent(run: AgentRunState, event: AgentRunEvent): AgentRunState | null {
  switch (event.type) {
    case 'request_started': {
      if (run.activityState === 'thinking' && isAgentRunLoading(run) && run.currentStep === INITIAL_STEP && !run.isNetworkError) {
        return null; // Ignore duplicate request_started while already thinking.
      }

      return { ...run, activityState: 'thinking', currentStep: INITIAL_STEP, isNetworkError: false };
    }
    case 'tool_progressed': {
      if (deriveEditorState(run) !== 'interactive') {
        return null; // Ignore tool progress while the editor is locked.
      }
      // Once the edit phase ends, the run stays loading in an explicit
      // awaiting-summary state. Ignore late progress so we do not resurrect a
      // stale step while the trailing assistant summary is still streaming.
      if (isAgentRunAwaitingPostEditSummary(run)) {
        return null; // Ignore progress after the edit phase already ended.
      }

      const metadata = getToolSessionMetadata(event.toolType);
      if (!metadata?.allowProgressEvent) {
        return null; // Ignore tools that do not publish step progress.
      }

      if (run.activityState === metadata.activityState && isAgentRunLoading(run) && run.currentStep === metadata.step) {
        return null; // Ignore duplicate progress for the same step.
      }

      return { ...run, activityState: metadata.activityState, currentStep: metadata.step };
    }
    case 'agent_write_started': {
      const editorState = deriveEditorState(run);
      if (editorState === 'review') {
        return null; // Persisted review is the committed end of the edit phase.
      }
      if (editorState !== 'interactive') {
        return null; // Ignore write-start unless the editor can transition into writing.
      }
      if (!isAgentRunLoading(run) && run.activityState !== 'idle') {
        return null; // Ignore late write-start after the run already ended.
      }

      // activityState === 'writing' derives editorState 'agent_writing'.
      return { ...run, activityState: 'writing', currentStep: EDITOR_WRITE_STEP };
    }
    case 'agent_write_finished': {
      if (deriveEditorState(run) !== 'agent_writing') {
        return null; // Ignore write-finished unless a write phase is active.
      }

      // Leaving 'writing' with reviewPending still false derives editorState back to 'interactive'.
      return { ...run, activityState: 'awaiting_summary', currentStep: null };
    }
    case 'review_ready': {
      if (deriveEditorState(run) !== 'agent_writing') {
        return null; // Ignore review-ready unless we are exiting a write phase.
      }

      // Durable pendingReview is committed; reviewPending derives editorState 'review'.
      return { ...run, reviewPending: true, activityState: 'awaiting_summary', currentStep: null };
    }
    case 'review_resolved': {
      if (deriveEditorState(run) !== 'review') {
        return null; // Ignore review-resolved unless review is currently active.
      }

      // Clearing reviewPending derives editorState back to 'interactive'.
      return {
        ...run,
        reviewPending: false,
        activityState: isAgentRunLoading(run) ? run.activityState : 'done',
        currentStep: null,
      };
    }
    case 'completed': {
      if (run.activityState === 'done' && !isAgentRunLoading(run) && run.currentStep == null) {
        return null; // Ignore duplicate completed after the run is already done.
      }

      return { ...run, activityState: 'done', currentStep: null };
    }
    case 'cancelled': {
      if (
        deriveEditorState(run) === 'interactive' &&
        run.activityState === 'cancelled' &&
        !isAgentRunLoading(run) &&
        run.currentStep == null &&
        !run.isNetworkError
      ) {
        return null; // Ignore duplicate cancelled after cancellation is already recorded.
      }

      // Clearing reviewPending derives editorState back to 'interactive'.
      return { ...run, reviewPending: false, activityState: 'cancelled', currentStep: null, isNetworkError: false };
    }
    case 'errored': {
      if (
        deriveEditorState(run) === 'interactive' &&
        run.activityState === 'errored' &&
        !isAgentRunLoading(run) &&
        run.currentStep == null &&
        run.isNetworkError === event.network
      ) {
        return null; // Ignore duplicate errored with the same network classification.
      }

      // Clearing reviewPending derives editorState back to 'interactive'.
      return { ...run, reviewPending: false, activityState: 'errored', currentStep: null, isNetworkError: event.network };
    }
  }
}
